import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Alert, EmptyState, Modal, Skeleton } from '@indipia/ui-core'
import { confirmShipReceipt, requestShip, sendShipArrivalNotice } from '../api/shipApi'
import { useMaterialOrderData } from '../hooks/useMaterialOrders'
import { useShipDeliveries } from '../hooks/useShipDeliveries'
import { useInvalidateSignals } from '../hooks/useTaskSignals'
import { isDirectOrderTask } from '../constants/materialOrder.logic'
import { SHIP_POPUP_TEXT, SHIP_TEXT, SHIP_TOAST, shipErrorMessage, shipNoticeToast, shipPopupPhotoTitle, shipReceiptPhotoNote, shipTodaySeoul, type ShipDeliveryItem, type ShipReceiptBody, type ShipRowButton } from '../constants/ship.logic'
import { ErrorBanner } from './AsyncState'
import { ShipColumnHeader } from './ship/ShipColumnHeader'
import { ShipDeliveryRow } from './ship/ShipDeliveryRow'
import { ShipPhotoViewerHost } from './ship/ShipPhotoViewerHost'
import { ShipReceiptConfirmModal } from './ship/ShipReceiptConfirmModal'
import { ShipSummaryChips } from './ship/ShipSummaryChips'

/**
 * 배송정보현황 · 요청 팝업 (C-4) — 간트/캘린더/공종 보기 상단 [파트너배정 / 알림발송 ▼] > 배송정보현황->요청.
 * 서버 C7 한 번으로 요약·행·정렬·사유·버튼 후보를 그린다(FE 는 정렬·분류를 다시 계산하지 않음).
 * 부모가 조건부 렌더링으로 마운트한다(닫으면 언마운트 → 쿼리 정지).
 * focusTaskId 는 딥링크(taskId)로 들어온 공종 — 그 공정의 모든 행을 강조하고 첫 행으로 스크롤한다.
 */
interface Props {
  onClose: () => void
  scheduleId: number
  siteName?: string
  focusTaskId?: number | null
  taskNameById: ReadonlyMap<number, string>
  /** '자재파트너가 시공' 공종 taskId(현장별 방식 반영 최종값) — 안내 노출·별도발주 공정 수 제외에 쓴다 */
  vendorConstructTaskIds?: readonly number[]
  onOpenForemanAssign?: (taskId: number) => void
}

type Action = Exclude<ShipRowButton, null>

export function ShipStatusPopup({ onClose, scheduleId, siteName, focusTaskId, taskNameById, vendorConstructTaskIds, onOpenForemanAssign }: Props) {
  const vendorConstructSet = new Set(vendorConstructTaskIds ?? [])

  const q = useShipDeliveries(scheduleId)
  const invalidateSignals = useInvalidateSignals()
  const today = shipTodaySeoul()

  const [busyId, setBusyId] = useState<number | null>(null)
  const [photoFor, setPhotoFor] = useState<{ item: ShipDeliveryItem; kind: 'LOAD' | 'UNLOAD' | null } | null>(null)
  const [receiptFor, setReceiptFor] = useState<ShipDeliveryItem | null>(null)
  const [receiptBusy, setReceiptBusy] = useState(false)
  const [receiptError, setReceiptError] = useState<string | null>(null)

  // '별도발주 공정 N' — 별도발주 공종 수(목록 건수가 아님, 자재파트너 시공 공종 제외). 조회 전·실패면 표시하지 않는다(값을 만들어 채우지 않음)
  const orders = useMaterialOrderData(scheduleId)
  const directCount = orders.status === 'ready'
    ? orders.tasks.filter(t => !vendorConstructSet.has(t.taskId) && isDirectOrderTask(orders.settings, t)).length
    : null

  // 사진 확대 보기(또는 사진 불러오는 중)가 열려 있는 동안 Esc 는 그 보기만 닫는다 — ui-core Modal 은 document 에서
  // Esc 를 받아 바깥 팝업도 같이 닫히므로, 창(window) 캡처 단계에서 먼저 받아 전파를 끊는다.
  const photoOpen = photoFor != null
  useEffect(() => {
    if (!photoOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      e.preventDefault()
      setPhotoFor(null)
      setBusyId(null)
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [photoOpen])

  // 직접 입고 확인 창이 열려 있는 동안 Esc 는 그 창만 닫는다(제출 중에는 닫지 않음) — 사진 보기와 같은 이유
  const receiptOpen = receiptFor != null
  useEffect(() => {
    if (!receiptOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      e.preventDefault()
      if (!receiptBusy) setReceiptFor(null)
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [receiptOpen, receiptBusy])

  const data = q.data
  const focusId = focusTaskId ?? null
  const focusExists = focusId != null && !!data && data.items.some(it => it.taskId === focusId)
  const firstFocusOvId = focusId != null && data ? data.items.find(it => it.taskId === focusId)?.orderVendorId ?? null : null

  // 강조 첫 행으로 스크롤 — 데이터 도착 후 1회.
  const scrolled = useRef(false)
  const setFirstFocusRef = useCallback((el: HTMLDivElement | null) => {
    if (el && !scrolled.current) {
      scrolled.current = true
      el.scrollIntoView({ block: 'center' })
    }
  }, [])
  useEffect(() => { scrolled.current = false }, [focusId])

  const afterWrite = useCallback(async () => {
    await Promise.all([q.refetch(), invalidateSignals()])
  }, [q, invalidateSignals])

  const handleError = useCallback((err: unknown) => {
    const { text, refetch } = shipErrorMessage(err)
    toast.error(text)
    if (refetch) void q.refetch()
  }, [q])

  const onAction = useCallback(async (item: ShipDeliveryItem, button: Action) => {
    if (busyId != null) return
    switch (button.kind) {
      case 'ASSIGN_FOREMAN':
        onClose()
        onOpenForemanAssign?.(item.taskId)
        return
      case 'REQUEST_NOW':
      case 'REREQUEST':
        setBusyId(item.orderVendorId)
        try {
          await requestShip(item.orderVendorId)
          toast.success(SHIP_TOAST.requested)
          await afterWrite()
        } catch (err) { handleError(err) } finally { setBusyId(null) }
        return
      case 'SEND_ARRIVAL_NOTICE':
        setBusyId(item.orderVendorId)
        try {
          const res = await sendShipArrivalNotice(item.orderVendorId)
          if (res.result === 'NO_FOREMAN') toast.error(shipNoticeToast(res.result))
          else toast.success(shipNoticeToast(res.result))
          await afterWrite()
        } catch (err) { handleError(err) } finally { setBusyId(null) }
        return
      case 'VIEW_PHOTOS':
        setBusyId(item.orderVendorId)
        setPhotoFor({ item, kind: null })
        return
      default:
        return // CALL_DRIVER(비활성)·CONFIRM_RECEIPT(대표 버튼이 아니라 보조 링크로 진입)
    }
  }, [busyId, afterWrite, handleError, onClose, onOpenForemanAssign])

  const onPhotoLink = useCallback((item: ShipDeliveryItem, kind: 'LOAD' | 'UNLOAD') => {
    if (busyId != null) return
    setBusyId(item.orderVendorId)
    setPhotoFor({ item, kind })
  }, [busyId])

  const openReceipt = useCallback((item: ShipDeliveryItem) => {
    if (busyId != null) return
    setReceiptError(null)
    setReceiptFor(item)
  }, [busyId])

  const submitReceipt = useCallback(async (body: ShipReceiptBody) => {
    if (!receiptFor || receiptBusy) return
    setReceiptBusy(true)
    setReceiptError(null)
    try {
      await confirmShipReceipt(receiptFor.orderVendorId, body)
      toast.success(SHIP_TOAST.receiptDone)
      setReceiptFor(null)
      await afterWrite()
    } catch (err) {
      const { text, refetch } = shipErrorMessage(err)
      if (refetch) {
        // 이미 입고됐거나 입고할 수 없는 상태로 바뀜 — 창을 닫고 목록을 새로 불러온다
        toast.error(text)
        setReceiptFor(null)
        void q.refetch()
      } else {
        setReceiptError(text)
      }
    } finally {
      setReceiptBusy(false)
    }
  }, [receiptFor, receiptBusy, afterWrite, q])

  const loading = q.isLoading
  const showError = !loading && q.isError

  return (
    <Modal open onClose={onClose} title={SHIP_TEXT.popupTitle} size="xl">
      {(siteName || directCount != null) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', marginBottom: 12 }}>
          {siteName && <span>{siteName}</span>}
          {directCount != null && <span>{SHIP_POPUP_TEXT.directCount(directCount)}</span>}
        </div>
      )}

      {vendorConstructSet.size > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Alert kind="info">{SHIP_POPUP_TEXT.vendorConstructNote}</Alert>
        </div>
      )}

      <div aria-busy={loading} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {loading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[0, 1, 2].map(i => <Skeleton key={i} height={72} />)}
          </div>
        )}

        {showError && (
          <ErrorBanner message={shipErrorMessage(q.error).text} onRetry={() => { void q.refetch() }} />
        )}

        {!loading && !showError && data && (
          <>
            {focusId != null && !focusExists && (
              <Alert kind="info">{SHIP_TEXT.focusMissing}</Alert>
            )}
            <ShipSummaryChips summary={data.summary} />
            {data.items.length === 0 ? (
              <EmptyState title={SHIP_TEXT.emptyTitle} />
            ) : (
              <>
                <ShipColumnHeader />
                <div role="list" aria-label={SHIP_POPUP_TEXT.listLabel} style={{ display: 'flex', flexDirection: 'column' }}>
                  {data.items.map(item => {
                    const highlighted = focusId != null && item.taskId === focusId
                    return (
                      <ShipDeliveryRow
                        key={item.orderVendorId}
                        ref={item.orderVendorId === firstFocusOvId ? setFirstFocusRef : undefined}
                        item={item}
                        taskName={taskNameById.get(item.taskId)}
                        today={today}
                        highlighted={highlighted}
                        canAssignForeman={onOpenForemanAssign != null}
                        loading={busyId === item.orderVendorId}
                        onAction={onAction}
                        onPhotoLink={onPhotoLink}
                        onConfirmReceipt={openReceipt}
                      />
                    )
                  })}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {photoFor && (
        <ShipPhotoViewerHost
          orderVendorId={photoFor.item.orderVendorId}
          title={shipPopupPhotoTitle(taskNameById.get(photoFor.item.taskId) ?? SHIP_POPUP_TEXT.taskNameMissing, photoFor.item.vendorName, shipReceiptPhotoNote(photoFor.item))}
          startKind={photoFor.kind}
          onClose={() => { setPhotoFor(null); setBusyId(null) }}
          onLoadingChange={l => { if (!l) setBusyId(null) }}
        />
      )}
      {receiptFor && (
        <ShipReceiptConfirmModal
          subject={`${taskNameById.get(receiptFor.taskId) ?? SHIP_POPUP_TEXT.taskNameMissing} · ${receiptFor.vendorName}`}
          today={today}
          busy={receiptBusy}
          error={receiptError}
          onSubmit={body => { void submitReceipt(body) }}
          onClose={() => setReceiptFor(null)}
        />
      )}
    </Modal>
  )
}
